import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { getItems, placeBid, getMyBids, logout, checkSession } from '../../../services/externalBiddingService';
import { getSettings } from '../../../models/settingsModel';
import { API_BASE_URL } from '../../../config';

/**
 * External Bidding Dashboard
 * Shows bidding items to authenticated employees
 */
const BiddingDashboard = ({ session, onLogout }) => {
  const [items, setItems] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('items'); // 'items' | 'mybids'
  const [selectedItem, setSelectedItem] = useState(null);
  const [bidAmount, setBidAmount] = useState('');
  const [notifyOutbid, setNotifyOutbid] = useState(true);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [now, setNow] = useState(new Date());
  const [settings, setSettings] = useState(null);

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await getSettings();
        if (response && response.status === 1 && response.data) {
          setSettings(response.data);
        }
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };
    loadSettings();
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Format time remaining
  const getTimeRemaining = useCallback((targetDate) => {
    const total = Date.parse(targetDate) - Date.parse(now);
    if (total <= 0) return null;
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
    const days = Math.floor(total / (1000 * 60 * 60 * 24));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }, [now]);

  // Load items
  const fetchItems = useCallback(async () => {
    try {
      const response = await getItems(session.session_token);
      if (response.status === 1) {
        setItems(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        onLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [session.session_token, onLogout]);

  // Load my bids
  const fetchMyBids = useCallback(async () => {
    try {
      const response = await getMyBids(session.session_token);
      if (response.status === 1) {
        setMyBids(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
    }
  }, [session.session_token]);

  // Initial load
  useEffect(() => {
    fetchItems();
    fetchMyBids();
  }, [fetchItems, fetchMyBids]);

  // Auto-refresh items every 30 seconds to catch admin closures
  useEffect(() => {
    if (!session?.session_token) return;
    
    const refreshTimer = setInterval(() => {
      fetchItems();
    }, 30000); // Refresh every 30 seconds
    
    return () => clearInterval(refreshTimer);
  }, [session?.session_token, fetchItems]);

  // Handle item selection for bidding
  const handleBidClick = (item) => {
    if (!item.is_open) {
      toast.warning('Bidding is not open for this item');
      return;
    }
    
    setSelectedItem(item);
    
    const currentHighest = parseFloat(item.highest_bid) || 0;
    const openingBid = parseFloat(item.opening_bid) || 0;
    const increment = parseFloat(item.increment_amount) || 0;
    
    let nextBid = currentHighest > 0 ? currentHighest + increment : openingBid;
    setBidAmount(nextBid.toFixed(2));
    
    // Check if user has already accepted disclaimer for this item
    const hasAccepted = item.my_bid && item.my_bid.disclaimer_accepted === 1;
    setDisclaimerAccepted(hasAccepted); // Pre-check if already accepted
  };

  // Submit bid
  const handleSubmitBid = async (e) => {
    e.preventDefault();
    if (!selectedItem || submitting) return;

    const amount = parseFloat(bidAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid bid amount');
      return;
    }

    setSubmitting(true);
    try {
      const response = await placeBid(session.session_token, selectedItem.id, amount, notifyOutbid, disclaimerAccepted);
      if (response.status === 1) {
        toast.success(response.message || 'Bid placed successfully!');
        setSelectedItem(null);
        setBidAmount('');
        setNotifyOutbid(true);
        setDisclaimerAccepted(false);
        fetchItems();
        fetchMyBids();
      } else {
        toast.error(response.message || 'Failed to place bid');
      }
    } catch (error) {
      console.error('Error placing bid:', error);
      toast.error(error.response?.data?.message || 'Failed to place bid');
    } finally {
      setSubmitting(false);
    }
  };

  // Logout
  const handleLogout = async () => {
    try {
      await logout(session.session_token);
    } catch (error) {
      console.error('Logout error:', error);
    }
    onLogout();
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">Loading bidding items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {settings?.site_logo_preview && settings.site_logo_preview !== 'Not available' && (
                <img 
                  src={settings.site_logo_preview} 
                  alt={settings?.site_name || 'Logo'} 
                  className="h-14 w-auto object-contain" 
                />
              )}
              <div className="border-l border-gray-200 pl-4">
                <h1 className="text-lg font-semibold text-gray-800">Employee Bidding</h1>
                <p className="text-sm text-gray-500">
                  Welcome, {session.employee.first_name} {session.employee.last_name}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('items')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'items'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Available Items ({items.filter(i => i.is_open).length})
            </button>
            <button
              onClick={() => setActiveTab('mybids')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'mybids'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Bids ({myBids.length})
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'items' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-max">
            {items.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                    d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No items available</h3>
                <p className="mt-1 text-sm text-gray-500">Check back later for new bidding opportunities.</p>
              </div>
            ) : (
              items.map((item) => (
                <ItemCard 
                  key={item.id} 
                  item={item} 
                  onBidClick={handleBidClick}
                  getTimeRemaining={getTimeRemaining}
                  formatCurrency={formatCurrency}
                />
              ))
            )}
          </div>
        ) : (
          <MyBidsList bids={myBids} formatCurrency={formatCurrency} />
        )}
      </div>

      {/* Bid Modal */}
      {selectedItem && (
        <BidModal
          item={selectedItem}
          bidAmount={bidAmount}
          setBidAmount={setBidAmount}
          notifyOutbid={notifyOutbid}
          setNotifyOutbid={setNotifyOutbid}
          disclaimerAccepted={disclaimerAccepted}
          setDisclaimerAccepted={setDisclaimerAccepted}
          biddingSettings={settings}
          onSubmit={handleSubmitBid}
          onClose={() => setSelectedItem(null)}
          submitting={submitting}
          formatCurrency={formatCurrency}
        />
      )}
    </div>
  );
};

/**
 * Item Card Component
 */
const ItemCard = ({ item, onBidClick, getTimeRemaining, formatCurrency }) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const timeRemaining = item.closing_date ? getTimeRemaining(item.closing_date) : null;
  const photoUrl = item.photos?.[0]?.photo_path ? `${API_BASE_URL}${item.photos[0].photo_path}` : null;

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden hover:shadow-md transition-shadow flex flex-col h-full">
      {/* Image */}
      <div className="aspect-video bg-gray-100 relative flex-shrink-0">
        {photoUrl ? (
          <img 
            src={photoUrl} 
            alt={item.title}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-12 h-12 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}
        
        {/* Status Badge */}
        <div className="absolute top-2 right-2">
          {item.is_open ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
              Open
            </span>
          ) : item.auction_closed ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Closed
            </span>
          ) : item.has_ended ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              Closed
            </span>
          ) : (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
              Not Started
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2">{item.title}</h3>
        
        {/* Description with Expander */}
        {item.description && (
          <div className="mb-3">
            <div 
              className={`text-sm text-gray-600 ${!descriptionExpanded ? 'line-clamp-2' : ''}`}
              dangerouslySetInnerHTML={{ __html: item.description }}
            />
            {item.description.length > 100 && (
              <button 
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                className="text-xs text-blue-600 hover:text-blue-800 mt-1 flex items-center"
              >
                {descriptionExpanded ? (
                  <>
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                    </svg>
                    Show less
                  </>
                ) : (
                  <>
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                    Show more
                  </>
                )}
              </button>
            )}
          </div>
        )}
        
        {/* Bid Info */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs text-gray-500">Current Bid</p>
            <p className="text-lg font-bold text-blue-600">
              {item.highest_bid ? formatCurrency(item.highest_bid) : formatCurrency(item.opening_bid)}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-500">Bids</p>
            <p className="text-lg font-semibold text-gray-700">{item.bid_count || 0}</p>
          </div>
        </div>

        {/* Time Remaining */}
        {timeRemaining && item.is_open && (
          <div className="flex items-center text-sm text-orange-600 mb-3">
            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {timeRemaining} remaining
          </div>
        )}

        {/* My Bid Indicator */}
        {item.my_bid && (
          <div className={`mb-3 p-2 rounded-lg ${item.my_bid.is_highest ? 'bg-green-50 border border-green-200' : 'bg-orange-50 border border-orange-200'}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs ${item.my_bid.is_highest ? 'text-green-700' : 'text-orange-700'}`}>
                  Your bid: <span className="font-semibold">{formatCurrency(item.my_bid.amount)}</span>
                </p>
                {!item.my_bid.is_highest && (
                  <p className="text-xs text-orange-600 mt-1">
                    Highest bid: <span className="font-semibold">{formatCurrency(item.highest_bid)}</span>
                  </p>
                )}
              </div>
              {item.my_bid.is_highest ? (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Highest
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  Outbid
                </span>
              )}
            </div>
          </div>
        )}

        {/* Bid Button - Pushed to bottom */}
        <button
          onClick={() => onBidClick(item)}
          disabled={!item.is_open || (item.my_bid && item.my_bid.is_highest)}
          className="mt-auto w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {!item.is_open ? 'Bidding Closed' : (item.my_bid && item.my_bid.is_highest) ? 'You Have Highest Bid' : 'Place Bid'}
        </button>
      </div>
    </div>
  );
};

/**
 * My Bids List Component
 */
const MyBidsList = ({ bids, formatCurrency }) => {
  if (bids.length === 0) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" 
            d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900">No bids yet</h3>
        <p className="mt-1 text-sm text-gray-500">Start bidding on items to see your bids here.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your Bid</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Highest Bid</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {bids.map((bid) => (
            <tr key={bid.id} className={bid.is_highest ? 'bg-green-50' : ''}>
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900">{bid.item_title}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm font-semibold text-gray-900">{formatCurrency(bid.amount)}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className="text-sm text-gray-700">{formatCurrency(bid.highest_bid || bid.amount)}</span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                {bid.is_winning ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    🏆 Winner
                  </span>
                ) : bid.item_status === 'closed' ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    Auction Closed
                  </span>
                ) : bid.is_highest ? (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Highest
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Outbid
                  </span>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {new Date(bid.created_at).toLocaleDateString()}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Bid Modal Component
 */
const BidModal = ({ item, bidAmount, setBidAmount, notifyOutbid, setNotifyOutbid, disclaimerAccepted, setDisclaimerAccepted, biddingSettings, onSubmit, onClose, submitting, formatCurrency }) => {
  const minBid = item.highest_bid 
    ? parseFloat(item.highest_bid) + parseFloat(item.increment_amount || 0)
    : parseFloat(item.opening_bid);
  
  // Check if user has already accepted disclaimer for this item
  const hasAcceptedDisclaimerForItem = item.my_bid && item.my_bid.disclaimer_accepted === 1;
  const showDisclaimer = biddingSettings?.site_bid_disclaimer && !hasAcceptedDisclaimerForItem;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        <div className="relative bg-white rounded-xl shadow-xl transform transition-all sm:max-w-lg sm:w-full mx-4">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Current Bid Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-500">Current Highest Bid</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {item.highest_bid ? formatCurrency(item.highest_bid) : formatCurrency(item.opening_bid)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Minimum Bid</p>
                  <p className="text-lg font-semibold text-blue-600">{formatCurrency(minBid)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Increment: {formatCurrency(item.increment_amount || 0)}
              </p>
            </div>

            {/* Bid Form */}
            <form onSubmit={onSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Bid Amount</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">R</span>
                  <input
                    type="number"
                    step="0.01"
                    min={minBid}
                    value={bidAmount}
                    onChange={(e) => setBidAmount(e.target.value)}
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                    placeholder="Enter amount"
                  />
                </div>
              </div>

              {/* Disclaimer Acceptance - Only show if not yet accepted for this item */}
              {showDisclaimer && (
                <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="mb-2 flex items-center">
                    <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <strong className="text-sm font-semibold text-yellow-800">Disclaimer</strong>
                  </div>
                  <div className="bg-white p-3 rounded border border-yellow-200 mb-3 text-sm text-gray-700 max-h-32 overflow-y-auto">
                    {biddingSettings.site_bid_disclaimer}
                  </div>
                  <label className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={disclaimerAccepted}
                      onChange={(e) => setDisclaimerAccepted(e.target.checked)}
                      className="mt-1 h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      required
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">
                      I have read and accept the disclaimer
                    </span>
                  </label>
                </div>
              )}

              {/* Notify on Outbid Toggle */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={notifyOutbid}
                      onChange={(e) => setNotifyOutbid(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-10 h-6 rounded-full shadow-inner transition-colors ${notifyOutbid ? 'bg-blue-600' : 'bg-gray-300'}`}></div>
                    <div className={`absolute w-4 h-4 bg-white rounded-full shadow top-1 transition-transform ${notifyOutbid ? 'translate-x-5' : 'translate-x-1'}`}></div>
                  </div>
                  <span className="ml-3 text-sm text-gray-700">Notify me by SMS if I'm outbid</span>
                </label>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting || parseFloat(bidAmount) < minBid || (showDisclaimer && !disclaimerAccepted)}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Placing...
                    </>
                  ) : (
                    'Place Bid'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BiddingDashboard;
